<template>
  <div>
    <app-breadcrumb :items="breadcrumbs" class="mb-24px"/>
    <a-row :gutter="32" class="mb-32px">
      <a-col :xs="24" :md="12" :lg="8">
        <div class="org-select-wrapper">
          <div class="org-select-field">
            <form-select
                :options="organizationsList"
                :placeholder="$t('label.organization')"
                :value="organizationId"
                :disabled="isSuperadmin"
                with-icon
                @change="organizationId = $event"
            >
              <template #prefix-icon>
                <list-icon/>
              </template>
            </form-select>
          </div>
          <a-tooltip
              v-if="isSuperadmin"
              :title="$t('text.org_select_superadmin_hint')"
              placement="right"
          >
            <a-icon type="question-circle" class="org-select-hint"/>
          </a-tooltip>
        </div>
      </a-col>
    </a-row>
    <app-tabs v-if="organizationId" :tabs="tabs" style="margin-left: 2px">
      <template #general>
        <general-tab :organization="organization"/>
      </template>
      <template #bases_pico>
        <bases-tab :organization="organization"/>
      </template>
      <template #import_of_users>
        <import-users-tab :organization-id="organizationId"/>
      </template>
      <template #notifications>
        <notifications-tab :organization-id="organizationId"/>
      </template>
    </app-tabs>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import AppBreadcrumb from '@/components/elements/AppBreadcrumb';
import AppTabs from '@/components/elements/AppTabs';
import FormSelect from '@/components/elements/FormSelect';
import GeneralTab from './components/GeneralTab';
import BasesTab from './components/BasesTab';
import ImportUsersTab from './components/ImportUsersTab';
import NotificationsTab from './components/NotificationsTab';
import ListIcon from '@/assets/icons/listIcon';

export default {
  components: {
    AppBreadcrumb,
    AppTabs,
    FormSelect,
    GeneralTab,
    BasesTab,
    ImportUsersTab,
    NotificationsTab,
    ListIcon
  },
  data: () => ({
    organizationId: null
  }),
  mounted() {
    this.$store.dispatch('organizationsStore/GET_DROPDOWN_LIST');
  },
  computed: {
    ...mapGetters({
      organizationsList: 'organizationsStore/DROPDOWN_LIST',
      organization: 'organizationsStore/ORGANIZATION',
      currentOrganizationId: 'getCurrentOrganizationId',
      mainRoleName: 'getMainRoleName'
    }),
    // Sur la page Settings (admin-only), un superadmin est toujours en « admin view » :
    // le choix d'organisation se fait alors via le switcher du header, pas ici.
    isSuperadmin() {
      return this.mainRoleName === 'superadmin';
    },
    // Organisation présélectionnée par défaut dans le sélecteur.
    defaultOrganizationId() {
      // Superadmin : hériter de l'organisation choisie en admin view (switcher header).
      if (this.isSuperadmin && this.currentOrganizationId
          && this.organizationsList.some(o => o.value === this.currentOrganizationId)) {
        return this.currentOrganizationId;
      }
      // Repli (admin) : première organisation de la liste (triée alphabétiquement).
      return this.organizationsList.length ? this.organizationsList[0].value : null;
    },
    breadcrumbs() {
      return [
        {name: this.$t('navigation.home'), route: 'Dashboard'},
        {name: this.$t('label.settings'), route: null}
      ];
    },
    tabs() {
      return ['general', 'bases_pico', 'import_of_users', 'notifications'];
    }
  },
  watch: {
    organizationsList: {
      immediate: true,
      handler(list) {
        if (list && list.length && !this.organizationId) {
          this.organizationId = this.defaultOrganizationId;
        }
      }
    },
    currentOrganizationId: {
      // Superadmin : le sélecteur d'org est verrouillé, l'organisation est
      // imposée par le switcher d'admin view (header). Settings doit donc suivre
      // currentOrganizationId de façon réactive — sans dépendre de la présence de
      // l'org dans le dropdown ni de l'ordre de chargement — pour que
      // GET_ORGANIZATION soit bien déclenché et que les onglets se remplissent.
      immediate: true,
      handler(id) {
        if (this.isSuperadmin && id) {
          this.organizationId = id;
        }
      }
    },
    organizationId: {
      // immediate: l'organisation par défaut est souvent posée dès `created`
      // (liste des organisations déjà en cache). Sans immediate, GET_ORGANIZATION
      // n'était jamais dispatché pour cet id initial et les onglets restaient vides.
      immediate: true,
      handler(id) {
        if (id) {
          this.$store.dispatch('organizationsStore/GET_ORGANIZATION', id);
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.org-select-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.org-select-field {
  flex: 1;
  min-width: 0;
}

.org-select-hint {
  flex-shrink: 0;
  color: #6886A6;
  font-size: 16px;
  cursor: help;
}
</style>